import { Archive, ArrowLeft, Building2, Ellipsis } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Badge from '@/components/primitives/Badge.jsx';
import Button from '@/components/primitives/Button.jsx';
import Icon from '@/components/primitives/Icon.jsx';
import Menu from '@/components/patterns/Menu.jsx';
import toastManager from '@/lib/toast/toastManager.js';
import hydrateThreads from '@/redux/actions/pieveActions/hydrateThreads.js';
import { archiveThread } from '@/redux/requests/threadArchiveRequests.js';
import { promoteThread } from '@/redux/requests/threadsRequests.js';
import { threadMeta } from '@/redux/selectors/pieveSelectors/threadCoreSelectors.js';
import { authUserId } from '@/redux/selectors/userSelectors.js';

/** The open conversation's title, whether the organization can see it, and its actions: share with the organization, archive. */
const ThreadHeader = ({ threadId }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const meta = useSelector((state) => threadMeta(state, threadId));
  const viewerId = useSelector(authUserId);
  const isShared = meta?.isCompany === true;
  const isOwner = !meta?.userId || String(meta.userId) === String(viewerId);

  const share = async () => {
    const res = await promoteThread({ threadId });
    if (res?.success === false) {
      toastManager.add({ title: 'Not shared', description: res.message || 'The conversation could not be shared.', type: 'error' });
      return;
    }
    toastManager.add({ title: 'Shared with your organization', description: 'Everyone in the organization can now open this conversation.', type: 'success' });
  };

  const archive = async () => {
    navigate('/');
    const res = await archiveThread({ threadId });
    if (res?.success === false) {
      toastManager.add({ title: 'Not archived', description: res.message || 'The conversation could not be archived.', type: 'error' });
      return;
    }
    toastManager.add({ title: 'Archived', description: 'Find it under Archived on the assistant home.', type: 'success' });
    // Going home reloads the list while the archive is still in flight, which can bring the conversation back.
    dispatch(hydrateThreads({ skipEvents: true }));
  };

  return (
    <header className="assistant-thread-header">
      <Link to="/" className="assistant-thread-header__back" aria-label="All conversations">
        <Icon icon={ArrowLeft} />
      </Link>
      <div className="assistant-thread-header__title-row">
        <h1 className="assistant-thread-header__title">{meta?.title || 'Conversation'}</h1>
        {isShared && (
          <Badge tone="brand">
            <Icon icon={Building2} size="sm" /> Organization
          </Badge>
        )}
      </div>
      {isOwner && (
        <Menu align="end" trigger={<Button variant="ghost" iconOnly iconStart={Ellipsis} label="Conversation actions" />}>
          {!isShared && (
            <Menu.Item icon={Building2} onClick={share}>
              Share with organization
            </Menu.Item>
          )}
          <Menu.Item icon={Archive} onClick={archive}>
            Archive
          </Menu.Item>
        </Menu>
      )}
    </header>
  );
};

export default ThreadHeader;
